/**
 * Confetti — small celebratory overlay (birthdays on home/people).
 *
 * Absolute layer over the parent (Card-sized by default): a handful of tiny
 * pieces fall in an endless cycle and spin as they go. RN Animated only, no
 * new dependencies — native driver off the web, transform/opacity-only.
 *
 * Per-piece params (position/size/color/timing) are derived deterministically
 * from the index via a tiny LCG hash, not `Math.random` — re-renders (and
 * snapshot tests) stay stable.
 *
 * Honors the OS "reduce motion" setting: renders nothing.
 */
import { useEffect, useRef, useState } from 'react'
import { Animated, Platform } from 'react-native'
import { Stack } from 'tamagui'

import { tone } from '../theme/tones'
import { prefersReducedMotion } from './motion'

const COLORS = ['gold', 'purple', 'green', 'red'] as const

// Deterministic pseudo-random in [0, 1) for (index, salt) — LCG-style hash.
const rnd = (i: number, salt: number) => (((i + 1) * 9301 + salt * 49297) % 233280) / 233280

interface PieceSpec {
  x: number // left, %
  size: number // px, 5–9
  color: string
  rect: boolean // true → rectangle (borderRadius 1), false → circle (999)
  duration: number // ms, 2800–4800
  delay: number // ms, 0–2500
  spin: 1 | -1
}

function pieceAt(i: number): PieceSpec {
  return {
    x: rnd(i, 1) * 100,
    size: 5 + rnd(i, 2) * 4,
    color: tone(COLORS[Math.floor(rnd(i, 3) * COLORS.length) % COLORS.length]).solid,
    rect: rnd(i, 4) < 0.5,
    duration: 2800 + rnd(i, 5) * 2000,
    delay: rnd(i, 6) * 2500,
    spin: rnd(i, 7) < 0.5 ? 1 : -1,
  }
}

function ConfettiPiece({ spec, height }: { spec: PieceSpec; height: number }) {
  const t = useRef(new Animated.Value(0)).current

  // Повтор — вручную, а не Animated.loop: в вебе луп с useNativeDriver проходит один круг и
  // замирает (кусочки падали раз после открытия страницы и висели внизу — 29.10.2026).
  // Каждый круг: с верха, с той же паузой перед стартом, что и раньше.
  useEffect(() => {
    let stopped = false
    const fall = () => {
      t.setValue(0)
      Animated.timing(t, { toValue: 1, duration: spec.duration, delay: spec.delay, useNativeDriver: Platform.OS !== 'web' }).start(
        ({ finished }) => {
          if (finished && !stopped) fall()
        },
      )
    }
    fall()
    return () => {
      stopped = true
      t.stopAnimation()
    }
  }, [t, spec.duration, spec.delay])

  const translateY = t.interpolate({ inputRange: [0, 1], outputRange: [-16, height + 16] })
  const rotate = t.interpolate({ inputRange: [0, 1], outputRange: ['0deg', `${360 * spec.spin}deg`] })

  return (
    <Animated.View
      style={{
        position: 'absolute',
        left: `${spec.x}%` as never,
        top: 0,
        width: spec.size,
        height: spec.size,
        borderRadius: spec.rect ? 1 : 999,
        backgroundColor: spec.color,
        transform: [{ translateY }, { rotate }],
      }}
    />
  )
}

/** Falling confetti pieces, clipped to the parent's rounded corners. */
export function Confetti({
  count = 12,
  borderRadius = 7,
  opacity = 0.9,
}: {
  count?: number
  borderRadius?: number
  opacity?: number
}) {
  const [height, setHeight] = useState(200)

  if (prefersReducedMotion()) return null

  return (
    <Stack
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      pointerEvents="none"
      overflow="hidden"
      borderRadius={borderRadius}
      opacity={opacity}
      onLayout={(e) => {
        const h = e.nativeEvent.layout.height
        if (h > 0) setHeight(h)
      }}
    >
      {Array.from({ length: count }, (_, i) => (
        <ConfettiPiece key={i} spec={pieceAt(i)} height={height} />
      ))}
    </Stack>
  )
}
